import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError, type Division, type DivisionTier, type DivisionTeamScore, type DivisionPlayerStat } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { displayClanName } from '../lib/displayName';
import { canWrite } from '../lib/roles';
import { hasOrgManagerMembership } from '../lib/orgAccess';
import { useEffectiveMe } from '../lib/viewAs';
import { DivisionsScoreboardPanel } from '../components/DivisionsScoreboardPanel';
import { findDivisionsStage, hasStagePipeline } from '../lib/pipelineView';
import { Layers, Crown, Swords, Users, ChevronDown, ChevronUp } from 'lucide-react';

const TIER_CONFIG: Record<DivisionTier, { label: string; icon: React.ReactNode }> = {
  PROS: {
    label: 'Pros',
    icon: <Crown className="h-5 w-5 text-ink-secondary" />,
  },
  AMATEUR_PROS: {
    label: 'Amateur Pros',
    icon: <Swords className="h-5 w-5 text-ink-secondary" />,
  },
  AMATEURS: {
    label: 'Amateurs',
    icon: <Users className="h-5 w-5 text-ink-secondary" />,
  },
  CHALLENGERS: {
    label: 'Challengers',
    icon: <Crown className="h-5 w-5 text-ink-secondary" />,
  },
  PRO_AMS_DIV_1: {
    label: 'PRO AMS Div 1',
    icon: <Swords className="h-5 w-5 text-ink-secondary" />,
  },
  PRO_AMS_DIV_2: {
    label: 'PRO AMS Div 2',
    icon: <Swords className="h-5 w-5 text-ink-secondary" />,
  },
  AMS_DIV_1: {
    label: 'AMS Div 1',
    icon: <Users className="h-5 w-5 text-ink-secondary" />,
  },
  AMS_DIV_2: {
    label: 'AMS Div 2',
    icon: <Users className="h-5 w-5 text-ink-secondary" />,
  },
  AMS_DIV_3: {
    label: 'AMS Div 3',
    icon: <Users className="h-5 w-5 text-ink-secondary" />,
  },
};

// Display order for division cards — strongest tier first.
const TIER_ORDER: DivisionTier[] = [
  'CHALLENGERS', 'PROS', 'AMATEUR_PROS', 'AMATEURS',
  'PRO_AMS_DIV_1', 'PRO_AMS_DIV_2', 'AMS_DIV_1', 'AMS_DIV_2', 'AMS_DIV_3',
];

// ── Division card ─────────────────────────────────────────────────────────────

const fmt = (n: number) => (n === 0 ? '–' : n.toLocaleString());
const teamLabel = (row: DivisionTeamScore) => row.displayName || displayClanName(row.clanName);

// Score table for a division whose `standings` are populated (SCORE comps).
// Rank · Team · one column per round · season Total, ranked highest → lowest.
function DivisionScoreTable({ division }: { division: Division }) {
  const [showPlayers, setShowPlayers] = useState(false);
  const config = TIER_CONFIG[division.tier] ?? TIER_CONFIG.AMATEURS;
  const rows = division.standings ?? [];
  const players: DivisionPlayerStat[] = division.players ?? [];
  const roundNums = division.roundNumbers
    ?? [...new Set(rows.flatMap(r => Object.keys(r.rounds).map(Number)))].sort((a, b) => a - b);

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        {config.icon}
        <h2 className="font-display text-lg font-semibold text-ink-primary">{config.label}</h2>
        <span className="ml-auto rounded-full bg-white/[0.05] px-2.5 py-0.5 font-mono text-xs text-ink-meta">
          {rows.length} team{rows.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-ink-meta">
              <th className="pb-2 pl-3 pr-2 text-center">#</th>
              <th className="pb-2 px-3 text-left">Team</th>
              {roundNums.map(rn => (
                <th key={rn} className="pb-2 px-3 text-right tabular-nums">R{rn}</th>
              ))}
              <th className="pb-2 pl-3 pr-3 text-right text-accent-500">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.clanName} className="odd:bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <td className={cn('py-2.5 pl-3 pr-2 text-center font-mono font-bold tabular-nums', i === 0 ? 'text-accent-500' : 'text-ink-meta')}>{i + 1}</td>
                <td className="py-2.5 px-3 font-medium text-ink-primary whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    {row.logoUrl && <img src={row.logoUrl} alt="" loading="lazy" className="h-5 w-5 shrink-0 rounded object-contain" />}
                    {teamLabel(row)}
                  </span>
                </td>
                {roundNums.map(rn => (
                  <td key={rn} className="py-2.5 px-3 text-right font-mono tabular-nums text-ink-secondary">{fmt(row.rounds?.[String(rn)] ?? 0)}</td>
                ))}
                <td className="py-2.5 pl-3 pr-3 text-right font-mono font-bold tabular-nums text-accent-500">{row.total.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {players.length > 0 && (
        <div className="mt-5 border-t border-white/[0.06] pt-4">
          <button
            onClick={() => setShowPlayers(v => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-secondary transition-colors hover:text-ink-primary"
          >
            {showPlayers ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Player stats ({players.length})
          </button>
          {showPlayers && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-ink-meta">
                    <th className="pb-2 px-3 text-left">Team</th>
                    <th className="pb-2 px-3 text-left">Player</th>
                    <th className="pb-2 px-3 text-right">Score</th>
                    <th className="pb-2 px-3 text-right">OBJ</th>
                    <th className="pb-2 px-3 text-right">Time</th>
                    <th className="pb-2 px-3 text-right">Plnt</th>
                    <th className="pb-2 px-3 text-right">Def</th>
                    <th className="pb-2 px-3 text-right">Ovl</th>
                    <th className="pb-2 px-3 text-right">K</th>
                    <th className="pb-2 px-3 text-right">D</th>
                    <th className="pb-2 pl-3 pr-3 text-right text-accent-500">KD</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p, i) => (
                    <tr key={`${p.team}-${p.player}-${i}`} className="odd:bg-white/[0.02] transition-colors hover:bg-white/[0.04]">
                      <td className="py-2 px-3 whitespace-nowrap text-ink-meta">{p.team}</td>
                      <td className="py-2 px-3 whitespace-nowrap font-medium text-ink-primary">{p.player}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-ink-secondary">{p.score.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-ink-meta">{p.objScore.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-ink-meta">{p.time.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-ink-meta">{p.plants}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-ink-meta">{p.defuses}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-ink-meta">{p.overloads}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-green-400">{p.kills}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-red-400">{p.deaths}</td>
                      <td className="py-2 pl-3 pr-3 text-right font-mono font-bold tabular-nums text-accent-500">{p.kd.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function DivisionCard({ division }: { division: Division }) {
  const config = TIER_CONFIG[division.tier] ?? TIER_CONFIG.AMATEURS;

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        {config.icon}
        <h2 className="font-display text-lg font-semibold text-ink-primary">{config.label}</h2>
        <span className="ml-auto rounded-full bg-white/[0.05] px-2.5 py-0.5 font-mono text-xs text-ink-meta">
          {division.clans.length} team{division.clans.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="space-y-1.5">
        {division.clans.map((clan, i) => (
          <div
            key={clan}
            className="flex items-center gap-3 rounded-lg bg-white/[0.04] px-4 py-2.5"
          >
            <span className="w-6 text-center font-mono text-sm font-bold tabular-nums text-ink-meta">{i + 1}</span>
            <span className="font-medium text-ink-primary">{displayClanName(clan)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Auto-split preview ────────────────────────────────────────────────────────

function CreateDivisionsPanel({ compId }: { compId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  // Load seeding standings for preview
  const { data: seeding } = useQuery({
    queryKey: ['seeding', compId],
    queryFn: () => api.seeding(compId),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => api.createDivisions({ compId }),
    onSuccess: () => {
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['divisions', compId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const ranked = seeding?.standings ?? [];
  const third = Math.ceil(ranked.length / 3);
  const preview: Partial<Record<DivisionTier, string[]>> = {
    PROS: ranked.slice(0, third).map((r: { clanName: string }) => r.clanName),
    AMATEUR_PROS: ranked.slice(third, third * 2).map((r: { clanName: string }) => r.clanName),
    AMATEURS: ranked.slice(third * 2).map((r: { clanName: string }) => r.clanName),
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={cn('btn-primary', MORPH)}>
        Create Divisions from Seeding Table
      </button>
    );
  }

  return (
    <Card className="mb-6">
      <h3 className="mb-3 font-display text-xs font-semibold uppercase tracking-[0.18em] text-ink-meta">
        Auto-Split Preview
      </h3>
      <p className="mb-4 text-xs text-ink-meta">
        Teams will be split into thirds based on seeding rank. You can reorder clans manually after creation if needed.
      </p>

      {ranked.length === 0 ? (
        <p className="mb-4 text-sm text-ink-secondary">No seeding scores found — run seeding rounds first.</p>
      ) : (
        <div className="mb-4 space-y-3">
          {(['PROS', 'AMATEUR_PROS', 'AMATEURS'] as DivisionTier[]).map(tier => (
            <div key={tier}>
              <p className="mb-1.5 font-display text-xs font-semibold uppercase tracking-wider text-ink-secondary">{TIER_CONFIG[tier].label}</p>
              <div className="flex flex-wrap gap-2">
                {(preview[tier] || []).map(clan => (
                  <span key={clan} className="rounded-full bg-white/[0.05] px-3 py-1 text-xs text-ink-body">{clan}</span>
                ))}
                {(preview[tier] || []).length === 0 && (
                  <span className="text-xs text-ink-meta">None</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="mb-3 text-xs text-red-400">{error}</p>}

      <div className="flex gap-2">
        <button onClick={() => mutation.mutate()} disabled={ranked.length === 0 || mutation.isPending}
          className={cn('btn-primary flex-1', MORPH)}>
          {mutation.isPending ? 'Creating…' : 'Confirm & Create Divisions'}
        </button>
        <button onClick={() => setOpen(false)} className="btn-ghost">
          Cancel
        </button>
      </div>
    </Card>
  );
}

// ── Divisions page ────────────────────────────────────────────────────────────

export function Divisions() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // View-as-aware copy — same idiom TournamentSetup uses (identity no-op
  // whenever no view-as is active), so a founder previewing a lower role or
  // an "org admin (scoped)" simulation correctly loses/gains this control.
  const effectiveMe = useEffectiveMe(me);
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [selectedCompId, setSelectedCompId] = useState('');

  const compId = selectedCompId
    || comps?.find(c => c.stage === 'ROUND_ROBIN' || c.stage === 'PLAYOFFS' || c.status === 'LIVE')?.id
    || comps?.[0]?.id
    || '';
  const activeComp = comps?.find(c => c.id === compId);

  // New-pipeline detection (strangler-fig coexistence — see lib/pipelineView.ts's
  // header comment). A comp that has composed ANY CompStage rows has moved off
  // the legacy skill-tier Divisions system onto the ordered stage pipeline;
  // same queryKey StagePipelinePanel/useCompChecklist already use for this
  // comp's stages, so this rides the existing cache rather than a fresh fetch.
  const { data: stages, isLoading: stagesLoading } = useQuery({
    queryKey: ['compStages', compId],
    queryFn: () => api.listCompStages(compId),
    enabled: !!compId,
  });
  const pipelineComp = hasStagePipeline(stages);
  const divisionsStage = findDivisionsStage(stages);

  // Pre-fetch alongside DivisionsScoreboardPanel's OWN identical query (same
  // queryKey => same cache entry, no extra network round trip) purely so this
  // page can tell "no fixtures yet" apart from "still loading" before handing
  // off to the panel — the panel itself renders null on empty, which would
  // otherwise read as a blank page here instead of an honest empty state.
  const scoreboardKey = ['divisionsScoreboard', compId, divisionsStage?.stageId];
  const { data: pipelineScoreboard, isLoading: scoreboardLoading } = useQuery({
    queryKey: scoreboardKey,
    queryFn: () => api.getDivisionsScoreboard(compId, divisionsStage!.stageId),
    enabled: !!compId && !!divisionsStage,
  });

  // Legacy skill-tier divisions (api.divisions) — only fetched for a comp that
  // hasn't moved onto the new pipeline (see pipelineComp above); a pipeline
  // comp renders the DivisionsScoreboardPanel branch below instead.
  const { data: divisions, isLoading } = useQuery({
    queryKey: ['divisions', compId],
    queryFn: () => api.divisions(compId),
    enabled: !!compId && !pipelineComp,
  });

  // Org-admin de-gate, widened 2026-07-24 (single role-spine rewire, ADMIN
  // can now run comps): founder/admin OR this comp's own org's ORG_ADMIN/
  // ORG_OWNER — mirrors ActivateCompetitionButton/EndCompetitionButton/
  // AdvanceStageButton's identical gate (retires the 2026-07-17 OWNER-ONLY
  // carve-out that excluded ORG_ADMIN here).
  const canCreateDivisions = canWrite(effectiveMe?.role) || hasOrgManagerMembership(effectiveMe, activeComp?.orgId);
  const hasDivisions = divisions && divisions.length > 0;

  return (
    <div className="max-w-4xl">
      <div className={cn('mb-6 flex flex-wrap items-center justify-between gap-4', SETTLE)} style={settleDelay(0)}>
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink-primary">
            <Layers className="h-6 w-6 text-ink-secondary" /> Divisions
          </h1>
          <p className="mt-1 text-sm text-ink-meta">
            Teams split into divisions after seeding — round robin played within each division
          </p>
        </div>

        {comps && comps.length > 1 && (
          <select value={compId} onChange={e => setSelectedCompId(e.target.value)} className="input">
            {comps.map(c => (
              <option key={c.id} value={c.id}>{c.name || c.id.replace('COMP#', '')}</option>
            ))}
          </select>
        )}
      </div>

      {pipelineComp ? (() => {
        // New-pipeline branch: this comp has composed real CompStage rows —
        // render the SAME per-division standings Setup's stage pipeline
        // already shows (DivisionsScoreboardPanel), instead of the legacy
        // skill-tier view below. No CreateDivisionsPanel here — that legacy
        // control only makes sense for a comp still on the old system.
        if (stagesLoading) return <PageSpinner />;

        if (!divisionsStage) {
          return (
            <div className={SETTLE} style={settleDelay(1)}>
              <EmptyState
                title="No divisions stage yet"
                message="This competition runs the new stage pipeline but hasn't composed a Divisions stage — its standings appear here once one is added and started (Setup → stage pipeline)."
                icon={<Layers className="h-12 w-12" strokeWidth={1.5} />}
              />
            </div>
          );
        }

        if (scoreboardLoading) return <PageSpinner />;

        if (!pipelineScoreboard || pipelineScoreboard.divisions.length === 0) {
          return (
            <div className={SETTLE} style={settleDelay(1)}>
              <EmptyState
                title="Divisions haven't been generated yet"
                message="This Divisions stage hasn't produced any fixtures yet — confirm its proposed split in Setup → stage pipeline to generate them."
                icon={<Layers className="h-12 w-12" strokeWidth={1.5} />}
              />
            </div>
          );
        }

        return (
          <div className={SETTLE} style={settleDelay(1)}>
            {/* /divisions and /groups both converge on this SAME Divisions-
                stage scoreboard once a comp runs the new pipeline — that
                overlap is ACCEPTABLE for now (a shared source beats either
                page showing empty). Which of the two nav entries should own
                this long-term is a separate product decision — not something
                to fake a distinction for here. */}
            <Card>
              <DivisionsScoreboardPanel compId={compId} stage={divisionsStage} />
            </Card>
          </div>
        );
      })() : (
        <>
          {/* Admin: create divisions if they don't exist yet — legacy only. */}
          {canCreateDivisions && !hasDivisions && compId && (
            <div className={cn('mb-6', SETTLE)} style={settleDelay(1)}>
              <CreateDivisionsPanel compId={compId} />
            </div>
          )}

          {isLoading ? (
            <PageSpinner />
          ) : !hasDivisions ? (
            <div className={SETTLE} style={settleDelay(2)}>
              <Card className="text-center">
                <Layers className="mx-auto mb-3 h-8 w-8 text-ink-meta" />
                <p className="text-sm text-ink-secondary">
                  Divisions haven't been created yet.
                  {canCreateDivisions ? ' Create them above once seeding is complete.' : ' Check back once seeding closes.'}
                </p>
              </Card>
            </div>
          ) : (() => {
            // Render EVERY division present, rank-ordered — the old hardcoded
            // PROS/AMATEUR_PROS/AMATEURS loop silently dropped any other tier
            // (the six EGL divisions rendered as a blank page).
            const sorted = [...divisions].sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));
            const hasScores = sorted.some(d => (d.standings?.length ?? 0) > 0);
            // Score tables are wide (a column per round) → stack full-width. Plain
            // roster cards stay in the compact 3-up grid.
            return hasScores ? (
              <div className={cn('space-y-6', SETTLE)} style={settleDelay(2)}>
                {sorted.map(div => (
                  div.standings?.length
                    ? <DivisionScoreTable key={div.tier} division={div} />
                    : <DivisionCard key={div.tier} division={div} />
                ))}
              </div>
            ) : (
              <div className={cn('grid gap-6 md:grid-cols-1 lg:grid-cols-3', SETTLE)} style={settleDelay(2)}>
                {sorted.map(div => <DivisionCard key={div.tier} division={div} />)}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
